import { useState, useEffect, useRef, useMemo } from 'react';
import { fetchResource } from '@platform/core/src/services/resourceApi';
import { useAuth } from '@platform/core/src/context/AuthContext'; // Import useAuth

/**
 * A hook that identifies IDs present in `requiredIds` but missing from `availableResources`,
 * fetches them from the API, and returns the fetched resources.
 *
 * @param {string} resourceType - The resource type string for the API (e.g., 'exercise_definition', 'workout_plan').
 * @param {Array<string>} requiredIds - List of IDs that are referenced and needed.
 * @param {Array<object>} availableResources - List of resources currently loaded.
 * @returns {Array<object>} An array of additional resources fetched from the server.
 */
export const useMissingResourceFetcher = (resourceType, requiredIds, availableResources) => {
  const [fetchedResources, setFetchedResources] = useState([]);
  const fetchingIdsRef = useRef(new Set());
  const fetchedIdsRef = useRef(new Set());
  
  // NEW: Check authentication status
  const { isAuthenticated } = useAuth(); 

  const availableIds = useMemo(() => {
    return new Set((availableResources || []).map(r => String(r.id)));
  }, [availableResources]);

  const stableRequiredIds = useMemo(() => {
    return Array.from(new Set(
      (requiredIds || [])
        .map(String)
        .filter(id => id && id !== 'null' && id !== 'undefined')
    )).sort();
  }, [requiredIds]);

  useEffect(() => {
    // Skip if not authenticated. Public views should rely on pre-loaded data 
    // or a separate public fetching mechanism.
    if (!isAuthenticated) return; 

    const missingIds = stableRequiredIds.filter(id =>
      !availableIds.has(id) &&
      !fetchedIdsRef.current.has(id) &&
      !fetchingIdsRef.current.has(id)
    );

    if (missingIds.length === 0) return;

    missingIds.forEach(id => fetchingIdsRef.current.add(id));

    const fetchMissing = async () => {
      const newResources = [];
      await Promise.all(missingIds.map(async (id) => {
        try {
          const resource = await fetchResource(resourceType, id);
          if (resource) {
            newResources.push(resource);
            fetchedIdsRef.current.add(id);
          } else {
            fetchedIdsRef.current.add(id); // Mark as fetched even if null to prevent retry loop
          }
        } catch (error) {
          console.error(`[useMissingResourceFetcher] Failed to fetch ${resourceType} ${id}:`, error);
          // Don't remove from fetchingIdsRef immediately to prevent rapid retry loops on error
          // fetchingIdsRef.current.delete(id); 
        }
      }));

      if (newResources.length > 0) {
        setFetchedResources(prev => {
            const prevIds = new Set(prev.map(r => String(r.id)));
            const uniqueNew = newResources.filter(r => !prevIds.has(String(r.id)));
            return [...prev, ...uniqueNew];
        });
      }
    };

    fetchMissing();
  }, [resourceType, stableRequiredIds, availableIds, isAuthenticated]); // Add isAuthenticated dependency

  return fetchedResources;
};